import { FC, useMemo } from "react";
import { Box, Colors, Tooltip, Typography } from "@analyticsMD/quil";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import moment from "moment-timezone";
import QCardReviewStatusResponse, {
  QCardReviewStatus,
} from "../types/QCardReviewStatus";

interface PhysicianReviewedBannerProps {
  reviewStatus: QCardReviewStatusResponse;
}

const REVIEWED_BANNER_BG = "#EDF7ED";
const REVIEWED_BANNER_TEXT = "#1E4620";
const REVIEWED_BANNER_ICON = "#418945";
const AWAITING_BANNER_BG = "#E5F6FD";
const AWAITING_BANNER_TEXT = "#014361";
const AWAITING_BANNER_ICON = "#1993D5";

const bannerBaseSx = {
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  minHeight: "30px",
  padding: "0 16px",
  borderBottom: `1px solid ${Colors.grey_20}`,
};

function formatReviewedTime(isoTimestamp: string, timezone: string): string {
  return moment(isoTimestamp).tz(timezone).format("h:mm a").toLowerCase();
}

function formatReviewedTooltip(
  reviewStatus: QCardReviewStatusResponse,
): string | undefined {
  if (
    reviewStatus.status !== QCardReviewStatus.REVIEWED ||
    !reviewStatus.first_reviewed_at
  ) {
    return undefined;
  }

  const openedAt = moment(reviewStatus.first_reviewed_at).tz(
    reviewStatus.timezone,
  );
  const physicianLabel = reviewStatus.opener_role || "Physician";
  return `${physicianLabel} · ${openedAt.format("MMM D, YYYY h:mm a")}`;
}

const PhysicianReviewedBanner: FC<PhysicianReviewedBannerProps> = ({
  reviewStatus,
}) => {
  const tooltipTitle = useMemo(
    () => formatReviewedTooltip(reviewStatus),
    [reviewStatus],
  );

  if (!reviewStatus.status) {
    return null;
  }

  if (reviewStatus.status === QCardReviewStatus.REVIEWED) {
    const reviewedTime = reviewStatus.first_reviewed_at
      ? formatReviewedTime(reviewStatus.first_reviewed_at, reviewStatus.timezone)
      : null;

    const label = reviewedTime
      ? `Physician reviewed · ${reviewedTime}`
      : "Physician reviewed";

    const reviewedBanner = (
      <Box
        tabIndex={tooltipTitle ? 0 : undefined}
        sx={{
          ...bannerBaseSx,
          backgroundColor: REVIEWED_BANNER_BG,
        }}
      >
        <CheckCircleIcon
          sx={{ fontSize: "1rem", color: REVIEWED_BANNER_ICON }}
          aria-hidden
        />
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: REVIEWED_BANNER_TEXT,
            lineHeight: 1.2,
          }}
        >
          {label}
        </Typography>
      </Box>
    );

    if (!tooltipTitle) {
      return reviewedBanner;
    }

    return (
      <Tooltip title={tooltipTitle} arrow placement="bottom">
        {reviewedBanner}
      </Tooltip>
    );
  }

  if (reviewStatus.status === QCardReviewStatus.NOT_YET_REVIEWED) {
    return (
      <Box
        sx={{
          ...bannerBaseSx,
          backgroundColor: AWAITING_BANNER_BG,
        }}
      >
        <InfoOutlinedIcon
          sx={{ fontSize: "1rem", color: AWAITING_BANNER_ICON }}
          aria-hidden
        />
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: AWAITING_BANNER_TEXT,
            lineHeight: 1.2,
          }}
        >
          Awaiting Physician Review
        </Typography>
      </Box>
    );
  }

  return null;
};

export default PhysicianReviewedBanner;
